// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type KpiBoxesProps, formatToMoney, formatToPercent } from '@shared/ui-kit';
import { useMemo } from 'react';
import { useTraderPortfolioData } from './use-trader-portfolio-data';
import { groupBy } from '@shared/data-access/utils/groupby';
import { capitalizeString } from '@shared/ui-kit/utils/capitalize-string';
import { useTraderAllocationAnalysis } from '@shared/portfolio-analytics/hooks/use-trader-allocation-analysis';
import { useSelector } from 'react-redux';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { useTranslation } from 'react-i18next';
export const useTraderAllocation = () => {
  const { t } = useTranslation('common');
  const {
    periodPicker,
    targetPortfolio,
    benchmark,
    isLoading: isLoadingPortfolio,
  } = useTraderPortfolioData();

  const selectedCurrency = useSelector(selectAccountCurrency);

  const totalInvestment = useMemo(
    () => targetPortfolio.reduce((acc, holding) => acc + holding.amount, 0),
    [targetPortfolio],
  );

  const equityAllocation = useMemo(() => {
    const equitySum = targetPortfolio
      .filter(holding => holding.asset_class === 'Equity')
      .reduce((acc, holding) => acc + holding.amount, 0);

    return totalInvestment > 0 ? equitySum / totalInvestment : 0;
  }, [targetPortfolio, totalInvestment]);

  const fixedIncomeAllocation = useMemo(() => {
    const fixedSum = targetPortfolio
      .filter(holding => holding.asset_class === 'Fixed Income')
      .reduce((acc, holding) => acc + holding.amount, 0);

    return totalInvestment > 0 ? fixedSum / totalInvestment : 0;
  }, [targetPortfolio, totalInvestment]);

  const kpis = useMemo<KpiBoxesProps[]>(
    () => [
      {
        text: t('common.investment'),
        value: formatToMoney(totalInvestment, undefined, 0),
        dataTestid: 'kpi-investment',
        icon: 'TrendingUp',
        color: 'success',
      },
      {
        text: t('common.equity-allocation'),
        value: formatToPercent(equityAllocation, 1),
        dataTestid: 'kpi-equity-allocation',
        icon: 'ChartPie',
        color: 'main',
      },
      {
        text: t('common.fixed-income-allocation'),
        value: formatToPercent(fixedIncomeAllocation, 1),
        dataTestid: 'kpi-fixed-income-allocation',
        icon: 'Layers',
        color: 'secondary',
      },
    ],
    [equityAllocation, fixedIncomeAllocation, totalInvestment, t],
  );

  const { assetAnalysis, isLoading: isLoadingAnalysis } =
    useTraderAllocationAnalysis({
      holdings: targetPortfolio,
      hist: periodPicker.period,
      benchmark,
    });

  const banks = useMemo(() => {
    const positionsByInstitution = groupBy(targetPortfolio, 'institution_name');
    const ids = Array.from(positionsByInstitution.keys());

    return {
      labels: ids.map(id => id ?? t('common.other')),
      values: ids.map(
        id =>
          (positionsByInstitution
            .get(id)
            ?.reduce((value, position) => value + position.amount, 0) ?? 0) /
          totalInvestment,
      ),
    };
  }, [targetPortfolio, totalInvestment, t]);

  const currency = useMemo(() => {
    const currencies = groupBy(targetPortfolio, 'currency');
    const ids = Array.from(currencies.keys());

    return {
      labels: ids.map(id => id ?? t('common.other')),
      values: ids.map(
        id =>
          (currencies.get(id)?.reduce((acc, holding) => acc + holding.amount, 0) ??
            0) / totalInvestment,
      ),
    };
  }, [targetPortfolio, totalInvestment, t]);

  const assetClass = useMemo(() => {
    const assetClasses = groupBy(targetPortfolio, 'asset_class');

    const ids = Array.from(assetClasses.keys());

    // istanbul ignore next
    return {
      labels: ids,
      values: ids.map(
        id =>
          (assetClasses
            .get(id)
            ?.reduce((acc, holding) => acc + holding.amount, 0) ?? 0) /
          totalInvestment,
      ),
    };
  }, [targetPortfolio, totalInvestment]);

  const vehicles = useMemo(() => {
    const vehicles = groupBy(targetPortfolio, 'asset_type');

    const ids = Array.from(vehicles.keys());

    // istanbul ignore next
    return {
      labels: ids.map(id => capitalizeString(id)),
      values: ids.map(
        id =>
          (vehicles.get(id)?.reduce((acc, holding) => acc + holding.amount, 0) ??
            0) / totalInvestment,
      ),
    };
  }, [targetPortfolio, totalInvestment]);

  const isLoading = useMemo(() => {
    return [isLoadingPortfolio, isLoadingAnalysis].some(Boolean);
  }, [isLoadingPortfolio, isLoadingAnalysis]);

  return {
    kpis,
    periodPicker,
    assetAnalysis,
    currency,
    assetClass,
    vehicles,
    banks,
    isLoading,
    selectedCurrency,
  };
};
